import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  useDistanceTypeServiceV1GetDistanceTypeQuery,
  useDistanceTypeServiceV1UpdateDistanceTypeMutation,
} from "_services/modules/distance-type";
import { DistanceCondition } from "_services/modules/distanceType";
import { yupNumTransform } from "_helper/formatters";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

export interface EditDistanceTypeFormValues {
  distanceTypeName: string;
  distanceCondition: DistanceCondition;
  distanceValue: number | string;
  distanceMin: number | string;
  distanceMax: number | string;
  description: string;
}

const schema = yup.object({
  distanceTypeName: yup.string().required("Distance Type Name is required").max(100, "Max 100 characters"),
  distanceCondition: yup.string().oneOf(["below", "between", "above"]).required("Distance Condition is required"),
  distanceValue: yup.mixed().when("distanceCondition", {
    is: (val: string) => val === "below" || val === "above",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Distance value is required").positive("Must be positive"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  distanceMin: yup.mixed().when("distanceCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Min value is required").min(0, "Must be >= 0"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  distanceMax: yup.mixed().when("distanceCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Max value is required").positive("Must be positive")
      .when("distanceMin", (distanceMin, schema) => {
        const minVal = Array.isArray(distanceMin) ? distanceMin[0] : distanceMin;
        if (typeof minVal === "number" && !isNaN(minVal)) {
          return schema.moreThan(minVal, "Max must be greater than Min");
        }
        return schema;
      }),
    otherwise: () => yup.mixed().notRequired(),
  }),
  description: yup.string().max(500, "Max 500 characters").default(""),
}).required();

const useEditDistanceTypeForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("distanceType");

  const { data: detail, isLoading } = useDistanceTypeServiceV1GetDistanceTypeQuery(
    { id: id! },
    { skip: !id }
  );

  const [updateDistanceType] = useDistanceTypeServiceV1UpdateDistanceTypeMutation();

  const {
    handleSubmit,
    formState: { errors },
    register,
    control,
    watch,
    setError,
    reset,
  } = useForm<EditDistanceTypeFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as never,
  });

  useEffect(() => {
    if (detail) {
      const resp = detail as typeof detail & { distance_min?: number; distance_max?: number };
      const condition = (resp.distance_condition as DistanceCondition) ?? "below";

      reset({
        distanceTypeName: resp.distance_type_name ?? "",
        distanceCondition: condition,
        distanceValue: condition === "below" ? (resp.distance_max ?? 0) : condition === "above" ? (resp.distance_min ?? 0) : "",
        distanceMin: condition === "between" ? (resp.distance_min ?? 0) : "",
        distanceMax: condition === "between" ? (resp.distance_max ?? 0) : "",
        description: resp.description ?? "",
      });
    }
  }, [detail, reset]);

  const distanceCondition = watch("distanceCondition");

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] = useState<EditDistanceTypeFormValues | null>(null);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const onSubmit = (data: EditDistanceTypeFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      await updateDistanceType({
        id,
        distanceTypeServiceV1UpdateDistanceTypeBody: {
          distance_type_name: pendingData.distanceTypeName,
          distance_condition: pendingData.distanceCondition,
          distance_min: pendingData.distanceCondition === "between"
            ? Number(pendingData.distanceMin)
            : (pendingData.distanceCondition === "below" ? 0 : Number(pendingData.distanceValue)),
          distance_max: pendingData.distanceCondition === "between"
            ? Number(pendingData.distanceMax)
            : (pendingData.distanceCondition === "below" ? Number(pendingData.distanceValue) : 0),
          description: pendingData.description,
        },
      }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      if (error?.status === 409) {
        setError("distanceTypeName", {
          type: "validate",
          message: t("error-duplicate-exists"),
        });
        setShowSaveModal(false);
      } else if (error?.status === 404) {
        setShowSaveModal(false);
        navigate(-1);
      } else {
        errorHandler(error);
        setShowSaveModal(false);
      }
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    handleSubmit,
    errors,
    register,
    control,
    distanceCondition,
    setError,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    isLoading,
    t,
  };
};

export default useEditDistanceTypeForm;
